<template>
  <Teleport to="body">
    <Transition name="offer-quick">
      <div
        v-if="visible"
        class="offer-quick-layer"
        :style="zIndex ? { zIndex } : null"
        @click.self="$emit('close')"
      >
        <aside
          class="offer-quick"
          role="dialog"
          aria-modal="true"
          aria-labelledby="offer-panel-title"
        >
          <header class="offer-quick__header">
            <div>
              <button
                v-if="backLabel"
                type="button"
                class="panel-back-link"
                @click="$emit('back')"
              >
                ← {{ backLabel }}
              </button>
              <p>Détail de l’annonce</p>
              <h2 id="offer-panel-title">
                {{ offer.title || "Annonce sans titre" }}
              </h2>
            </div>
            <button
              type="button"
              class="close-button"
              aria-label="Fermer le détail"
              @click="$emit('close')"
            >
              ×
            </button>
          </header>

          <div class="offer-quick__body">
            <section
              class="offer-gallery"
              aria-labelledby="offer-gallery-title"
            >
              <div class="offer-gallery__heading">
                <h3 id="offer-gallery-title">Images</h3>
                <span v-if="loading && !galleryImages.length">Chargement…</span>
                <span v-else
                  >{{ galleryImages.length }} image{{
                    galleryImages.length > 1 ? "s" : ""
                  }}</span
                >
              </div>

              <button
                type="button"
                class="offer-gallery__main"
                :class="{ 'offer-gallery__main--empty': mainImageError }"
                :aria-label="
                  galleryImages.length
                    ? 'Afficher l’image en grand'
                    : 'Aucune image disponible'
                "
                :disabled="!galleryImages.length"
                @click="openViewer(selectedImageIndex)"
              >
                <img
                  v-if="galleryImages.length && !mainImageError"
                  :src="selectedImageUrl"
                  :alt="`Image ${selectedImageIndex + 1} de ${offer.title || 'l’annonce'}`"
                  @error="mainImageError = true"
                />
                <span v-else aria-hidden="true">◎</span>
                <span
                  v-if="galleryImages.length && !mainImageError"
                  class="offer-gallery__zoom"
                  >Agrandir</span
                >
              </button>

              <div
                v-if="galleryImages.length > 1"
                class="offer-gallery__thumbs"
                aria-label="Choisir une image"
              >
                <button
                  v-for="(image, index) in galleryImages"
                  :key="`${image.image}-${index}`"
                  type="button"
                  :class="{ active: selectedImageIndex === index }"
                  :aria-label="`Afficher l’image ${index + 1}`"
                  :aria-pressed="selectedImageIndex === index"
                  @click="selectImage(index)"
                >
                  <img
                    :src="getImageUrl(image)"
                    :alt="`Miniature ${index + 1}`"
                    loading="lazy"
                  />
                </button>
              </div>
            </section>

            <div class="offer-meta">
              <span class="source-badge">{{ sourceLabel }}</span>
              <p class="offer-id">#{{ offer._key || "—" }}</p>
            </div>
            <section class="panel-section">
              <h3>Suivi</h3>
              <dl class="detail-list">
                <div>
                  <dt>Statut</dt>
                  <dd><span class="status-dot"></span>{{ statusLabel }}</dd>
                </div>
                <div>
                  <dt>{{ priceLabel }}</dt>
                  <dd class="price-value">
                    {{ formattedPrice }}
                    <button
                      v-if="canQuickPrice"
                      type="button"
                      class="quick-price"
                      :class="{ 'quick-price--busy': priceBusy }"
                      :disabled="priceBusy"
                      :aria-label="
                        priceBusy
                          ? 'Lecture du prix en cours'
                          : 'Actualiser le prix'
                      "
                      :title="
                        priceBusy
                          ? 'Lecture du prix…'
                          : 'Actualiser seulement le prix'
                      "
                      @click="refreshPrice"
                    >
                      <span
                        v-if="priceBusy"
                        class="quick-price__spinner"
                      ></span>
                      <span v-else aria-hidden="true">↻</span>
                    </button>
                  </dd>
                </div>

                <div>
                  <dt>Échéance</dt>
                  <dd>{{ formattedEnd }}</dd>
                </div>
                <div>
                  <dt>Rappel</dt>
                  <dd>{{ reminders || "5 minutes avant la fin" }}</dd>
                </div>
              </dl>
              <p
                v-if="priceMessage"
                class="quick-price__message"
                :class="{ 'quick-price__message--error': priceError }"
                role="status"
              >
                {{ priceMessage }}
              </p>
            </section>
            <section v-if="offer._key" class="panel-section">
              <PriceGuide
                :offer-id="String(offer._key)"
                :current-price="offer.price"
                :currency="offer.currency || 'JPY'"
              />
            </section>
            <section class="panel-section">
              <h3>Description</h3>
              <p v-if="offer.description" class="offer-description">
                {{ offer.description }}
              </p>
              <p v-else class="panel-note">
                Aucune description disponible pour cette annonce.
              </p>
            </section>
          </div>

          <!-- Opened from Échéances: its reminders can be cancelled here -->
          <div v-if="canCancelReminders && offer._key" class="reminders-bar">
            <span class="reminders-bar__status"
              >🔔 Rappels prévus : {{ reminders }}</span
            >
            <button
              type="button"
              class="link-button link-button--muted"
              @click="$emit('cancel-reminders', offer)"
            >
              Annuler les rappels
            </button>
          </div>
          <div v-if="showOwnership && offer._key" class="ownership-bar">
            <template v-if="offer.owns">
              <span class="ownership-bar__status"
                >✓ Remportée — dans vos achats</span
              >
              <button
                type="button"
                class="link-button"
                @click="$emit('open-acquisition', offer)"
              >
                Voir dans mes achats →
              </button>
              <button
                v-if="offer.package_id"
                type="button"
                class="link-button"
                @click="$emit('open-package', offer.package_id)"
              >
                Colis {{ offer.packageRef }} →
              </button>
              <button
                type="button"
                class="link-button link-button--muted"
                :disabled="ownershipLoading"
                @click="$emit('toggle-ownership', offer)"
              >
                {{
                  ownershipLoading ? "Enregistrement…" : "Retirer de mes achats"
                }}
              </button>
            </template>
            <template v-else>
              <span class="ownership-bar__status">{{
                isEnded
                  ? "Enchère terminée : l’avez-vous remportée ?"
                  : "Enchère en cours"
              }}</span>
              <button
                type="button"
                class="won-button"
                :disabled="ownershipLoading"
                @click="$emit('toggle-ownership', offer)"
              >
                {{
                  ownershipLoading
                    ? "Enregistrement…"
                    : "✓ Marquer comme remportée"
                }}
              </button>
              <button
                type="button"
                class="link-button link-button--muted"
                @click="$emit('unfollow', offer)"
              >
                Ne plus suivre
              </button>
            </template>
          </div>
          <footer class="offer-quick__footer">
            <ListingLinks v-if="offer.links" :links="offer.links" />
            <a
              v-else-if="externalUrl"
              :href="externalUrl"
              target="_blank"
              rel="noopener noreferrer"
              class="secondary-button"
              >Voir sur {{ sourceLabel }}</a
            >
            <button
              type="button"
              class="primary-button"
              @click="$emit('refresh', offer)"
            >
              Actualiser l’annonce
            </button>
          </footer>
        </aside>
      </div>
    </Transition>

    <Transition name="image-viewer">
      <div
        v-if="viewerOpen"
        class="image-viewer"
        :style="{ zIndex: (zIndex || 1000) + 100 }"
        role="dialog"
        aria-modal="true"
        aria-label="Image de l’annonce en grand"
        @click.self="closeViewer"
      >
        <div class="image-viewer__topbar">
          <span>{{ viewerImageIndex + 1 }} / {{ galleryImages.length }}</span>
          <button
            type="button"
            aria-label="Fermer l’image"
            @click="closeViewer"
          >
            ×
          </button>
        </div>
        <button
          v-if="viewerImageIndex > 0"
          type="button"
          class="image-viewer__nav image-viewer__nav--previous"
          aria-label="Image précédente"
          @click="previousImage"
        >
          ‹
        </button>
        <img
          :src="viewerImageUrl"
          :alt="`Image ${viewerImageIndex + 1} de ${offer.title || 'l’annonce'}`"
        />
        <button
          v-if="viewerImageIndex < galleryImages.length - 1"
          type="button"
          class="image-viewer__nav image-viewer__nav--next"
          aria-label="Image suivante"
          @click="nextImage"
        >
          ›
        </button>
      </div>
    </Transition>
  </Teleport>
</template>

<script>
import { computed, onMounted, onUnmounted, ref, watch } from "vue";
import config from "@/config/config.js";
import ListingLinks from "../common/ListingLinks.vue";
import PriceGuide from "../common/PriceGuide.vue";
import axios from "axios";
import { hasQuickPrice, itemUrl, sourceLongLabel } from "../../sources";
import { notifyChanged } from "../../detailStack";
import { formatDate, formatRemaining, parseTime, useNow } from "../../clock";
export default {
  name: "OfferQuickPanel",
  components: { ListingLinks, PriceGuide },
  props: {
    visible: { type: Boolean, default: false },
    offer: { type: Object, default: () => ({}) },
    loading: { type: Boolean, default: false },
    ownershipLoading: { type: Boolean, default: false },
    showOwnership: { type: Boolean, default: false },
    // Reminders of the offer ("1 h et 5 min avant la fin"), and whether they
    // can be cancelled from the sheet (opened from Échéances)
    reminders: { type: String, default: "" },
    canCancelReminders: { type: Boolean, default: false },
    // Opened over another sheet (see detailStack.js)
    backLabel: { type: String, default: "" },
    zIndex: { type: Number, default: 0 },
  },
  emits: [
    "back",
    "open-package",
    "close",
    "refresh",
    "toggle-ownership",
    "price-updated",
    "cancel-reminders",
    "open-acquisition",
    "unfollow",
  ],
  setup(props, { emit }) {
    const selectedImageIndex = ref(0);
    const viewerImageIndex = ref(0);
    const viewerOpen = ref(false);
    const mainImageError = ref(false);

    const sourceLabel = computed(() => sourceLongLabel(props.offer.source));

    // Quick price (↻): one page of the site read again, without the photos
    // nor the translation of "Actualiser l'annonce"
    const priceBusy = ref(false);
    const priceMessage = ref("");
    const priceError = ref(false);
    let priceTimer = null;
    const canQuickPrice = computed(
      () =>
        Boolean(props.offer._key) &&
        hasQuickPrice(props.offer.source) &&
        props.offer.final_price == null &&
        !isEnded.value,
    );
    const showPriceMessage = (text, error = false) => {
      priceMessage.value = text;
      priceError.value = error;
      clearTimeout(priceTimer);
      priceTimer = setTimeout(() => (priceMessage.value = ""), 6000);
    };
    const refreshPrice = async () => {
      if (priceBusy.value) return;
      const key = props.offer._key;
      priceBusy.value = true;
      try {
        const { data } = await axios.post(
          `${config.apiUrl}/offers/${key}/price`,
        );
        const fields = {
          price: data.price,
          bids: data.bids,
          endtime: data.endtime,
          endtime_utc: data.endtime_utc,
          timestamp_user: data.timestamp_user,
        };
        emit("price-updated", { _key: key, ...fields });
        notifyChanged("offer", { _key: key, ...fields });
        showPriceMessage(
          data.end_moved
            ? "Prix à jour. La fin de l’enchère a été repoussée : vos alertes suivent."
            : data.recent
              ? "Prix déjà relu il y a moins de 30 s."
              : "Prix à jour.",
        );
      } catch (error) {
        showPriceMessage(
          error.response?.data?.error ||
            "Le prix n’a pas pu être lu. Réessayez dans un instant.",
          true,
        );
      } finally {
        priceBusy.value = false;
      }
    };
    // Annonces Yahoo : photos téléchargées sur le CDN ; Mercari / Vinted (veille) :
    // miniature distante du site
    // offer.images are downloaded to the CDN when the offer is parsed (even
    // when stored as source URLs): only the thumbnail of an offer followed
    // from the "veille", not parsed yet, is shown from the source site.
    const galleryImages = computed(() => {
      const images = Array.isArray(props.offer.images)
        ? props.offer.images.filter(Boolean)
        : [];
      if (images.length || !props.offer.thumbnail_url) {
        return images.map((image) => ({ image, external: false }));
      }
      return [{ image: props.offer.thumbnail_url, external: true }];
    });
    const getImageUrl = ({ image, external }) => {
      if (external) return String(image);
      const imageName = String(image).split(/[\\/]/).pop().split(/[?#]/)[0];
      return `${config.cdnUrl}/images/${props.offer._key}/${imageName}`;
    };
    const selectedImageUrl = computed(() =>
      galleryImages.value[selectedImageIndex.value]
        ? getImageUrl(galleryImages.value[selectedImageIndex.value])
        : "",
    );
    const viewerImageUrl = computed(() =>
      galleryImages.value[viewerImageIndex.value]
        ? getImageUrl(galleryImages.value[viewerImageIndex.value])
        : "",
    );
    // Page of the offer on its site; offers from the "veille": "<source>-<id>"
    const externalUrl = computed(
      () =>
        props.offer.url ||
        itemUrl(
          props.offer.source,
          String(props.offer._key || "").replace(`${props.offer.source}-`, ""),
        ) ||
        "",
    );
    // Shared clock: the end status and the remaining time count down
    const now = useNow(1000);
    const isEnded = computed(
      () =>
        Boolean(props.offer.endtime) &&
        parseTime(props.offer.endtime) <= now.value,
    );
    const statusLabel = computed(() => {
      if (props.offer.owns) return "Remportée";
      // Sites without an end date: sold or removed (checked every 6 h)
      if (props.offer.availability === "sold")
        return `Vendue sur ${sourceLongLabel(props.offer.source)}`;
      if (props.offer.availability === "removed") return "Annonce retirée";
      return isEnded.value || props.offer.closed ? "Terminée" : "En cours";
    });
    const formattedEnd = computed(() => {
      const date = formatDate(props.offer.endtime, {
        dateStyle: "medium",
        timeStyle: "short",
      });
      if (!date) return "Non renseignée";
      return isEnded.value
        ? date
        : `${date} · dans ${formatRemaining(props.offer.endtime, now.value)}`;
    });
    const formattedPrice = computed(() => {
      if (
        props.offer.price === null ||
        props.offer.price === undefined ||
        props.offer.price === ""
      )
        return "Non renseigné";
      const price = Number(props.offer.price);
      return Number.isFinite(price)
        ? new Intl.NumberFormat("fr-FR", {
            style: "currency",
            currency: props.offer.currency || "JPY",
            maximumFractionDigits:
              (props.offer.currency || "JPY") === "JPY" ? 0 : 2,
          }).format(price)
        : "Non renseigné";
    });
    const formattedUpdatedAt = computed(() => {
      if (!props.offer.timestamp_user) return "Non renseignée";
      const rawTimestamp = props.offer.timestamp_user;
      const numericTimestamp = Number(rawTimestamp);
      const date =
        typeof rawTimestamp === "number" || /^\d+$/.test(String(rawTimestamp))
          ? new Date(
              numericTimestamp < 100000000000
                ? numericTimestamp * 1000
                : numericTimestamp,
            )
          : new Date(parseTime(rawTimestamp));
      if (Number.isNaN(date.getTime())) return "Non renseignée";
      return new Intl.DateTimeFormat("fr-FR", {
        dateStyle: "medium",
        timeStyle: "short",
      }).format(date);
    });
    // Final price: read just after the end of the auction
    const priceLabel = computed(() => {
      if (props.offer.final_price != null)
        return props.offer.final_status === "unsold"
          ? "Invendue, prix de départ"
          : "Prix final";
      return formattedUpdatedAt.value === "Non renseignée"
        ? "Prix actuel"
        : `Prix au ${formattedUpdatedAt.value}`;
    });

    const selectImage = (index) => {
      selectedImageIndex.value = index;
      mainImageError.value = false;
    };
    const openViewer = (index) => {
      if (galleryImages.value.length) {
        viewerImageIndex.value = index;
        viewerOpen.value = true;
      }
    };
    const closeViewer = () => {
      viewerOpen.value = false;
    };
    const previousImage = () => {
      if (viewerImageIndex.value > 0) viewerImageIndex.value -= 1;
    };
    const nextImage = () => {
      if (viewerImageIndex.value < galleryImages.value.length - 1)
        viewerImageIndex.value += 1;
    };
    const handleEscape = (event) => {
      if (!props.visible) return;
      if (event.key === "Escape")
        viewerOpen.value ? closeViewer() : emit("close");
      if (viewerOpen.value && event.key === "ArrowLeft") previousImage();
      if (viewerOpen.value && event.key === "ArrowRight") nextImage();
    };

    watch(
      () => props.offer._key,
      () => {
        selectedImageIndex.value = 0;
        viewerOpen.value = false;
        mainImageError.value = false;
      },
    );
    watch(
      () => props.visible,
      (visible) => {
        if (!visible) viewerOpen.value = false;
      },
    );
    onMounted(() => document.addEventListener("keydown", handleEscape));
    onUnmounted(() => {
      document.removeEventListener("keydown", handleEscape);
      clearTimeout(priceTimer);
    });

    return {
      canQuickPrice,
      priceBusy,
      priceError,
      priceMessage,
      refreshPrice,
      closeViewer,
      externalUrl,
      isEnded,
      formattedEnd,
      formattedPrice,
      galleryImages,
      getImageUrl,
      mainImageError,
      nextImage,
      openViewer,
      previousImage,
      priceLabel,
      selectImage,
      selectedImageIndex,
      selectedImageUrl,
      sourceLabel,
      statusLabel,
      viewerImageIndex,
      viewerImageUrl,
      viewerOpen,
    };
  },
};
</script>

<style scoped>
/* Opened over another sheet: way back to it */
.panel-back-link {
  display: inline-block;
  margin: 0 0 10px;
  padding: 0;
  border: 0;
  background: none;
  color: #2e6098;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
}

.offer-quick-layer {
  position: fixed;
  inset: 0;
  z-index: 1000;
  background: rgba(15, 23, 36, 0.38);
  backdrop-filter: blur(2px);
}
.offer-quick {
  position: absolute;
  top: 0;
  right: 0;
  display: flex;
  width: min(500px, 100%);
  height: 100%;
  flex-direction: column;
  background: #fff;
  box-shadow: -20px 0 50px rgba(15, 23, 36, 0.14);
  padding-top: var(--safe-top);
  padding-bottom: var(--safe-bottom);
}
.offer-quick__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 20px;
  padding: 25px 26px;
  border-bottom: 1px solid #e4e9ef;
}
.offer-quick__header p {
  margin: 0 0 6px;
  color: #168a64;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}
.offer-quick__header h2 {
  margin: 0;
  font-size: 19px;
  line-height: 1.4;
}
.close-button {
  display: grid;
  width: 36px;
  height: 36px;
  flex: 0 0 36px;
  place-items: center;
  border: 1px solid #d9e0e8;
  border-radius: 8px;
  background: #fff;
  color: #657083;
  font-size: 22px;
  cursor: pointer;
}
.offer-quick__body {
  flex: 1;
  overflow: auto;
  padding: 26px;
}
.offer-gallery {
  padding-bottom: 22px;
}
.offer-gallery__heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}
.offer-gallery__heading h3 {
  margin: 0;
  font-size: 13px;
}
.offer-gallery__heading span {
  color: #85909e;
  font-size: 11px;
}
.offer-gallery__main {
  position: relative;
  display: grid;
  width: 100%;
  height: 260px;
  overflow: hidden;
  place-items: center;
  padding: 0;
  border: 1px solid #e0e6ec;
  border-radius: 10px;
  background: #f3f6f8;
  color: #8b96a5;
  cursor: zoom-in;
}
.offer-gallery__main:disabled {
  cursor: default;
}
.offer-gallery__main img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}
.offer-gallery__zoom {
  position: absolute;
  right: 10px;
  bottom: 10px;
  padding: 6px 9px;
  border-radius: 6px;
  background: rgba(15, 23, 36, 0.8);
  color: #fff;
  font-size: 11px;
  font-weight: 700;
}
.offer-gallery__thumbs {
  display: flex;
  gap: 8px;
  margin-top: 10px;
  padding-bottom: 3px;
  overflow-x: auto;
}
.offer-gallery__thumbs button {
  width: 58px;
  height: 58px;
  flex: 0 0 58px;
  overflow: hidden;
  padding: 0;
  border: 2px solid transparent;
  border-radius: 8px;
  background: #f3f6f8;
  cursor: pointer;
}
.offer-gallery__thumbs button.active {
  border-color: #168a64;
}
.offer-gallery__thumbs img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.offer-meta {
  display: flex;
  align-items: center;
  gap: 10px;
  padding-bottom: 22px;
}
.source-badge {
  display: inline-flex;
  padding: 5px 8px;
  border-radius: 6px;
  background: #f0f3f6;
  color: #4d596a;
  font-size: 11px;
  font-weight: 700;
}
.offer-id {
  margin: 0;
  color: #85909e;
  font-size: 12px;
}
.panel-section {
  padding: 24px 0;
  border-top: 1px solid #e8ecf1;
}
.panel-section h3 {
  margin: 0 0 15px;
  font-size: 13px;
}
.detail-list {
  margin: 0;
}
.detail-list > div {
  display: flex;
  justify-content: space-between;
  gap: 20px;
  padding: 10px 0;
}
.detail-list dt {
  color: #7a8594;
  font-size: 12px;
}
.detail-list dd {
  display: flex;
  align-items: center;
  gap: 7px;
  margin: 0;
  color: #354153;
  font-size: 12px;
  font-weight: 650;
  text-align: right;
}
.reminders-bar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 8px 14px;
  padding: 12px 26px;
  border-top: 1px solid #e4e9ef;
  background: #fffaf2;
}
.reminders-bar__status {
  color: #7a4b0b;
  font-size: 13px;
  font-weight: 650;
}
.quick-price {
  display: inline-grid;
  width: 26px;
  height: 26px;
  place-items: center;
  padding: 0;
  border: 1px solid #d9e0e8;
  border-radius: 7px;
  background: #fff;
  color: #2e6098;
  font-size: 15px;
  line-height: 1;
  cursor: pointer;
}
.quick-price:hover:not(:disabled) {
  border-color: #2e6098;
  background: #f2f7fc;
}
.quick-price:focus-visible {
  outline: 2px solid #2e6098;
  outline-offset: 2px;
}
.quick-price--busy {
  cursor: progress;
}
/* Mini loader while the price is read */
.quick-price__spinner {
  width: 13px;
  height: 13px;
  border: 2px solid #c9d8ea;
  border-top-color: #2e6098;
  border-radius: 50%;
  animation: quick-price-spin 0.7s linear infinite;
}
@keyframes quick-price-spin {
  to {
    transform: rotate(360deg);
  }
}
@media (prefers-reduced-motion: reduce) {
  .quick-price__spinner {
    animation-duration: 2s;
  }
}
.quick-price__message {
  margin: 4px 0 0;
  color: #1d7a57;
  font-size: 12px;
}
.quick-price__message--error {
  color: #b42318;
}
.status-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #26a678;
}
.panel-note {
  margin: 0;
  padding: 14px;
  border-radius: 8px;
  background: #f5f8fa;
  color: #657183;
  font-size: 12px;
  line-height: 1.6;
}
.offer-description {
  max-height: 260px;
  margin: 0;
  overflow: auto;
  color: #536073;
  font-size: 12px;
  line-height: 1.65;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}
.offer-quick__footer {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  padding: 18px 26px;
  border-top: 1px solid #e4e9ef;
}
.primary-button,
.secondary-button {
  display: inline-flex;
  min-height: 40px;
  align-items: center;
  justify-content: center;
  padding: 0 15px;
  border: 1px solid #172235;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 700;
  text-decoration: none;
  cursor: pointer;
}
.primary-button {
  flex: 1;
  background: #172235;
  color: #fff;
}
.secondary-button {
  background: #fff;
  color: #455165;
}
.image-viewer {
  position: fixed;
  inset: 0;
  z-index: 1100;
  display: grid;
  place-items: center;
  padding: 70px;
  background: rgba(8, 14, 24, 0.94);
}
.image-viewer > img {
  max-width: 100%;
  max-height: calc(100vh - 100px);
  object-fit: contain;
}
.image-viewer__topbar {
  position: absolute;
  top: calc(20px + var(--safe-top));
  right: 24px;
  left: 24px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 18px;
  color: #fff;
  font-size: 13px;
}
.image-viewer__topbar button,
.image-viewer__nav {
  display: grid;
  place-items: center;
  border: 1px solid rgba(255, 255, 255, 0.35);
  background: rgba(255, 255, 255, 0.12);
  color: #fff;
  cursor: pointer;
}
.image-viewer__topbar button {
  width: 40px;
  height: 40px;
  border-radius: 9px;
  font-size: 25px;
}
.image-viewer__nav {
  position: absolute;
  top: 50%;
  width: 48px;
  height: 64px;
  border-radius: 10px;
  font-size: 40px;
  transform: translateY(-50%);
}
.image-viewer__nav--previous {
  left: 22px;
}
.image-viewer__nav--next {
  right: 22px;
}
.offer-quick-enter-active,
.offer-quick-leave-active,
.image-viewer-enter-active,
.image-viewer-leave-active {
  transition: opacity 0.2s;
}
.offer-quick-enter-active .offer-quick,
.offer-quick-leave-active .offer-quick {
  transition: transform 0.24s ease;
}
.offer-quick-enter-from,
.offer-quick-leave-to,
.image-viewer-enter-from,
.image-viewer-leave-to {
  opacity: 0;
}
.offer-quick-enter-from .offer-quick,
.offer-quick-leave-to .offer-quick {
  transform: translateX(100%);
}
@media (max-width: 520px) {
  .offer-quick__header,
  .offer-quick__body {
    padding: 20px;
  }
  .offer-quick__footer {
    padding: 16px 20px;
    flex-direction: column-reverse;
  }
  .offer-gallery__main {
    height: 220px;
  }
  .image-viewer {
    padding: 60px 12px 20px;
  }
  .image-viewer__nav {
    width: 40px;
    height: 54px;
  }
  .image-viewer__nav--previous {
    left: 8px;
  }
  .image-viewer__nav--next {
    right: 8px;
  }
}
/* Ownership (won / lost) actions */
.ownership-bar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  padding: 14px 26px;
  border-top: 1px solid #e4e9ef;
  background: #f7fafc;
}
.ownership-bar__status {
  flex: 1 1 100%;
  color: #4d596a;
  font-size: 12px;
  font-weight: 650;
}
.won-button {
  min-height: 38px;
  padding: 0 14px;
  border: 1px solid #b9d4ee;
  border-radius: 8px;
  background: #edf4fb;
  color: #2e6098;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
}
.won-button:hover {
  background: #e1ecf8;
}
.link-button {
  padding: 0;
  border: 0;
  background: none;
  color: #2e6098;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
}
.link-button--muted {
  margin-left: auto;
  color: #8a4b47;
}
.won-button:disabled,
.link-button:disabled {
  opacity: 0.55;
  cursor: wait;
}
@media (max-width: 520px) {
  .ownership-bar {
    padding: 14px 20px;
  }
}
</style>
